import React, { useEffect, useState } from 'react';
import { auth, db } from '../../../firebase/config';
import { doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import Button from '../../../components/Button';
import TextField from '../../../components/TextField';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiTrendingUp } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import AddCompanyModal from '../components/AddCompanyModal/index';
import CompanyDetailsModal from '../components/CompanyDetailsModal';
import Modal from '../../../components/Modal';
import { fetchCompaniesData, fetchCompanyBankId, type Company } from '../service/companiesService';
import Table, { Column } from '../../../components/Table';

const PAREN_ABBREV: Record<string, string> = {
    PVT: 'Pvt',
    LTD: 'Ltd',
    LLC: 'Llc',
    LLP: 'Llp',
    PLC: 'Plc',
    INC: 'Inc',
};

/** Trim full string, unify unicode/fullwidth punctuation to ASCII, collapse all spaces. */
function normalizeCompanyNameRaw(name: string | undefined | null): string {
    if (name == null) return '';
    let s = String(name);
    // Unicode / odd spaces → regular space, then collapse
    s = s.replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, ' ');
    // Fullwidth parens → ASCII so token parsing works
    s = s.replace(/\uFF08/g, '(').replace(/\uFF09/g, ')');
    // Trim whole string early
    s = s.trim().replace(/\s+/g, ' ');
    return s.trim();
}

/**
 * Normalize "Whatever Ltd (Pvt)" / "... Limited (Pvt)" at end to "... (Pvt) Ltd"
 * so the legal suffix reads consistently regardless of messy leading text after trim.
 */
function reorderLtdPvtAtEnd(s: string): string {
    const t = s.trim();
    const m = t.match(/^(.+)\s+(Limited|Ltd\.?)\s*\(\s*[pP][vV][tT]\s*\)\s*$/i);
    if (!m) return t;
    const prefix = m[1].trim();
    const ltdKind = m[2];
    const ltd =
        /^limited$/i.test(ltdKind) ? 'Ltd' : /\.$/.test(ltdKind) ? 'Ltd.' : 'Ltd';
    return `${prefix} (Pvt) ${ltd}`;
}

/** Split glued parentheses e.g. Services(Pvt)Ltd → separate tokens before formatting. */
function tokenizeCompanyDisplayName(raw: string): string[] {
    const spaced = raw
        .trim()
        .replace(/([^\s])(\()/g, '$1 $2')
        .replace(/(\))([^\s])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
    if (!spaced) return [];
    return spaced.split(/\s+/).filter(Boolean);
}

/** One segment outside parentheses (letters/word chars + optional trailing punctuation). */
function titleCasePlainWord(segment: string): string {
    if (!segment.length) return segment;
    const abbrev = formatAbbrevToken(segment);
    if (abbrev) return abbrev;

    const m = segment.match(/^([a-zA-Z0-9'&-]+)(\W*)$/);
    if (!m) {
        return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    }
    const [, core, trailing] = m;
    const cased = core.charAt(0).toUpperCase() + core.slice(1).toLowerCase();
    return cased + trailing;
}

/** Canonical short legal tokens (Pvt, Ltd.) including optional trailing dots. */
function formatAbbrevToken(word: string): string | null {
    const m = word.match(/^([A-Za-z]+)([\W]*)$/);
    if (!m) return null;
    const [, letters, trailing] = m;
    const canon = PAREN_ABBREV[letters.toUpperCase()];
    return canon ? canon + trailing : null;
}

/** Text inside ( … ): abbreviations like PVT → Pvt; otherwise title case per word. */
function formatParenInner(inner: string): string {
    return inner
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => formatAbbrevToken(w) ?? titleCasePlainWord(w))
        .join(' ');
}

/** Title-style display per word; parenthetical chunks use formatParenInner; fixes Services(Pvt) Ltd. */
function formatCompanyDisplayName(name: string | undefined | null): string {
    const normalized = normalizeCompanyNameRaw(name);
    const reordered = reorderLtdPvtAtEnd(normalized);
    const tokens = tokenizeCompanyDisplayName(reordered);
    if (!tokens.length) return '';
    let out = tokens
        .map((token) => {
            if (!token.length) return token;
            if (token.startsWith('(') && token.endsWith(')') && token.length > 2) {
                const inner = token.slice(1, -1).trim();
                if (!inner) return token;
                return `(${formatParenInner(inner)})`;
            }
            return titleCasePlainWord(token);
        })
        .join(' ')
        .trim();
    out = out.replace(/\(\s*[pP][vV][tT]\s*\)/g, '(Pvt)');
    out = out.replace(/\(\s*Pvt\s*\)\s*([Ll][Tt][Dd]\.?)\b/gi, (_m, ltd: string) => {
        const ltdNorm = /\.$/.test(ltd) ? 'Ltd.' : 'Ltd';
        return `(Pvt) ${ltdNorm}`;
    });
    return out.replace(/\s+/g, ' ').trim();
}

export default function Companies() {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [filtered, setFiltered] = useState<Company[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [companyTypes, setCompanyTypes] = useState<string[]>([]);
    const [bankId, setBankId] = useState<string | null>(null);
    const [unassignTarget, setUnassignTarget] = useState<Company | null>(null);
    const [unassignSubmitting, setUnassignSubmitting] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const fetchedBankId = await fetchCompanyBankId(uid, isBankUser);
                console.log('Fetched bankId:', fetchedBankId);
                if (fetchedBankId) {
                    setBankId(fetchedBankId);
                    const companiesData = await fetchCompaniesData(uid, isBankUser);
                    console.log('Companies data received in component:', companiesData);
                    setCompanies(companiesData);
                    setFiltered(companiesData);

                    // Extract unique company types
                    const typesSet = new Set<string>();
                    companiesData.forEach(company => {
                        if (company.companyType) {
                            typesSet.add(company.companyType);
                        }
                    });
                    setCompanyTypes(Array.from(typesSet));
                }
            } catch (err: any) {
                showToast('Failed to load companies data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = companies;

        // Apply filter based on selected company type
        if (selectedFilter && selectedFilter !== 'All') {
            list = list.filter(company => company.companyType === selectedFilter);
        }

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(company => {
                const companyName = (company.companyName || '').toString().toLowerCase();
                const companyId = (company.companyId || '').toString().toLowerCase();
                const companyNo = (company.companyNo || '').toString();
                return companyName.includes(term) || companyId.includes(term) || companyNo.includes(term);
            });
        }

        // Sort by company name alphabetically
        list.sort((a, b) => {
            const nameA = (a.companyName || '').toString();
            const nameB = (b.companyName || '').toString();
            return nameA.localeCompare(nameB);
        });

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, companies, selectedFilter]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setFiltered(companies);
        setCurrentPage(1);
    };

    const handleStatCardClick = (companyType: string) => {
        setSelectedFilter(selectedFilter === companyType ? null : companyType);
        setCurrentPage(1);
    };

    const openDetailsModal = (company: Company) => {
        setSelectedCompany(company);
        setShowDetailsModal(true);
    };

    const handleCompanyAdded = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const companiesData = await fetchCompaniesData(uid, isBankUser);

            // Extract unique company types
            const typesSet = new Set<string>();
            companiesData.forEach(c => {
                if (c.companyType) typesSet.add(c.companyType);
            });
            setCompanyTypes(Array.from(typesSet));

            // Clear any active filter so the newly added vendor is always visible
            setSelectedFilter(null);
            setSearchTerm('');

            // Sort alphabetically and set both states explicitly
            const sorted = [...companiesData].sort((a, b) =>
                (a.companyName || '').localeCompare(b.companyName || '')
            );
            setCompanies(sorted);
            setFiltered(sorted);

            setCurrentPage(1);
            setShowAddModal(false);
            showToast('✅ Vendor added successfully!', 'success');
        } catch (err: any) {
            console.error('Error refreshing companies:', err);
            showToast('❌ Failed to refresh companies', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (company: Company) => {
        if (!bankId || !company.id) return;

        const newStatus = company.status === 'Active' ? 'Inactive' : 'Active';

        setLoading(true);
        try {
            const companyRef = doc(db, 'banks', bankId, 'companies', company.id);
            await updateDoc(companyRef, {
                status: newStatus,
                updatedAt: new Date()
            });

            // Refresh companies list with proper enrichment
            const companiesData = await fetchCompaniesData(uid!, isBankUser);
            setCompanies(companiesData);

            // Extract unique company types
            const typesSet = new Set<string>();
            companiesData.forEach(c => {
                if (c.companyType) {
                    typesSet.add(c.companyType);
                }
            });
            setCompanyTypes(Array.from(typesSet));

            showToast(`✅ Company status updated to ${newStatus}!`, 'success');
        } catch (err: any) {
            console.error('Error updating company status:', err);
            showToast('❌ Failed to update company status.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const closeUnassignModal = () => {
        if (unassignSubmitting) return;
        setUnassignTarget(null);
    };

    const executeUnassignCompany = async (company: Company) => {
        if (!bankId || !company.id || !uid) return;

        const isGloballyLinked = Boolean(
            company.companyId && (company.id === company.companyId || company.isSelectedCompany)
        );

        setUnassignSubmitting(true);
        try {
            const companyRef = doc(db, 'banks', bankId, 'companies', company.id);
            await deleteDoc(companyRef);

            if (isGloballyLinked && company.companyId) {
                const globalCompanyRef = doc(db, 'companies', company.companyId);
                const globalSnap = await getDoc(globalCompanyRef);
                if (globalSnap.exists()) {
                    const currentData = globalSnap.data();
                    const bankClientArray = (currentData.bankClient || []) as { BankId?: string }[];
                    const nextBankClient = bankClientArray.filter((entry) => entry.BankId !== bankId);
                    if (nextBankClient.length !== bankClientArray.length) {
                        await updateDoc(globalCompanyRef, { bankClient: nextBankClient });
                    }
                }
            }

            const companiesData = await fetchCompaniesData(uid, isBankUser);
            setCompanies(companiesData);
            const typesSet = new Set<string>();
            companiesData.forEach((c) => {
                if (c.companyType) typesSet.add(c.companyType);
            });
            setCompanyTypes(Array.from(typesSet));
            if (selectedCompany?.id === company.id) {
                setShowDetailsModal(false);
                setSelectedCompany(null);
            }
            showToast('✅ Vendor unassigned successfully.', 'success');
            setUnassignTarget(null);
        } catch (err: any) {
            console.error('Error unassigning company:', err);
            showToast('❌ Failed to unassign vendor.', 'error');
        } finally {
            setUnassignSubmitting(false);
        }
    };

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Calculate company statistics by type
    const totalCompanies = companies.length;
    const companyTypeCounts = React.useMemo(() => {
        const counts: Record<string, number> = {};
        companies.forEach(company => {
            const type = company.companyType || 'Unknown';
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    }, [companies]);

    const columns: Column<Company>[] = [
        {
            header: 'Company Name',
            width: '12%',
            render: (company) => (
                <strong style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {formatCompanyDisplayName(company.companyName)}
                </strong>
            )
        },
        {
            header: 'Type',
            width: '7%',
            render: (company) => (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {company.companyType || 'N/A'}
                </span>
            )
        },
        {
            header: 'Phone',
            width: '10%',
            render: (company) => (
                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} className="text-gray-600 dark:text-gray-400">
                    {company.companyNo || 'N/A'}
                </span>
            )
        },
        {
            header: 'Email',
            width: '12%',
            render: (company) => (
                <span style={{ wordWrap: 'break-word', whiteSpace: 'normal' }} className="text-gray-600 dark:text-gray-400">
                    {company.email || 'N/A'}
                </span>
            )
        },
        {
            header: 'Contact Person Detail',
            width: '34%',
            render: (company) => {
                const hasContact = company.contactPersonName || company.contactMobileNumber || company.contactEmail || (company.secondaryEmails && company.secondaryEmails.length > 0);
                if (!hasContact) return <span className="text-gray-400 italic">No contact details</span>;
                return (
                    <div className="flex flex-col gap-1 text-xs text-gray-700 dark:text-gray-300">
                        {company.contactPersonName && (
                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                                {company.contactPersonName}
                            </div>
                        )}
                        {company.contactMobileNumber && (
                            <div className="flex items-center gap-1">
                                <span className="text-gray-400">Mob:</span>
                                <span>{company.contactMobileNumber}</span>
                            </div>
                        )}
                        {company.contactEmail && (
                            <div className="flex items-center gap-1">
                                <span className="text-gray-400">Email:</span>
                                <span className="break-all">{company.contactEmail}</span>
                            </div>
                        )}
                        {company.secondaryEmails && company.secondaryEmails.length > 0 && (
                            <div className="mt-1 flex flex-col gap-0.5">
                                <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Secondary Email(s):</span>
                                <div className="flex flex-wrap gap-1">
                                    {company.secondaryEmails.map((email) => (
                                        <span
                                            key={email}
                                            className="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
                                        >
                                            {email}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
        }
    ];

    if (isBankUser) {
        columns.push({
            header: 'Status',
            width: '6%',
            align: 'center',
            render: (company) => (
                <label className="relative inline-flex cursor-pointer items-center">
                    <input
                        type="checkbox"
                        checked={company.status === 'Active'}
                        onChange={() => handleStatusToggle(company)}
                        className="peer sr-only"
                        disabled={loading}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 transition-colors duration-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                        {company.status || 'Active'}
                    </span>
                </label>
            )
        });
        columns.push({
            header: 'Action',
            width: '14%',
            render: (company) => (
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={() => openDetailsModal(company)}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                        aria-label="Edit company"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={() => setUnassignTarget(company)}
                        disabled={loading || unassignSubmitting}
                        className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-all hover:border-red-400 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900/40 dark:bg-gray-800 dark:text-red-400 dark:hover:border-red-500 dark:hover:bg-red-950/30 dark:focus:ring-red-600"
                        aria-label="Unassign vendor from bank"
                    >
                        Unassign
                    </button>
                </div>
            )
        });
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading companies...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Statistics Cards - Company Types */}
                <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${companyTypes.length > 2 ? 'lg:grid-cols-4' : 'lg:grid-cols-' + Math.min(companyTypes.length + 1, 4)}`}>
                    {/* Total Companies Card */}
                    <div className="rounded-xl border-2 border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-black/30">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Vendors</p>
                                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{totalCompanies}</p>
                            </div>
                            <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                            </div>
                        </div>
                        {/* Mini Graph */}
                        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div className="h-full w-full bg-gray-500 dark:bg-gray-500"></div>
                        </div>
                    </div>

                    {/* Company Type Cards */}
                    {companyTypes.map((type) => {
                        const count = companyTypeCounts[type] || 0;
                        const percentage = totalCompanies > 0 ? (count / totalCompanies) * 100 : 0;
                        return (
                            <div
                                key={type}
                                onClick={() => handleStatCardClick(type)}
                                className={`rounded-xl border-2 cursor-pointer transition-all p-6 shadow-sm ${selectedFilter === type
                                    ? 'border-gray-900 bg-gray-100 dark:border-gray-100 dark:bg-gray-800'
                                    : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-black/30 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{type}</p>
                                        <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{count}</p>
                                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{percentage.toFixed(1)}% of total</p>
                                    </div>
                                    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-700">
                                        <Icon icon={FiTrendingUp} className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                                    </div>
                                </div>
                                {/* Mini Graph */}
                                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                                    <div
                                        className="h-full bg-gray-500 dark:bg-gray-500 transition-all duration-500"
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id || Math.random().toString()}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No vendors found matching your search' : 'No vendors available'}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                    title="Vendors"
                    description="Manage and view all vendor information"
                    headerActions={
                        (isBankUser ) && (
                            <Button
                                onClick={() => setShowAddModal(true)}
                                className="flex items-center gap-2"
                            >
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Add Vendor
                            </Button>
                        )
                    }
                    filters={
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <Icon
                                    icon={FiSearch}
                                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search by company name or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                />
                            </div>
                            <div className="relative">
                                <select
                                    value={selectedFilter || 'All'}
                                    onChange={(e) => setSelectedFilter(e.target.value === 'All' ? null : e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                >
                                    <option value="All">All Types</option>
                                    {companyTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="secondary"
                                    onClick={handleReset}
                                    className="text-sm whitespace-nowrap"
                                >
                                    Clear Search
                                </Button>
                            )}
                        </div>
                    }
                />
            </div>

            {/* Toast */}
            {
                toast.show && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toast.message}
                                type={toast.type}
                                onClose={() => setToast({ ...toast, show: false })}
                            />
                        </div>
                    </div>
                )
            }

            {/* Modals */}
            <AddCompanyModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleCompanyAdded}
                bankId={bankId || undefined}
                existingCompanies={companies}
            />

            <Modal
                isOpen={unassignTarget !== null}
                onClose={closeUnassignModal}
                title="Unassign vendor?"
                size="sm"
                closeOnBackdropClick={!unassignSubmitting}
            >
                <div className="space-y-4 px-5 py-5 sm:px-6">
                    <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        Unassign{' '}
                        <strong className="text-gray-900 dark:text-gray-100">
                            {unassignTarget
                                ? `"${
                                      formatCompanyDisplayName(unassignTarget.companyName) ||
                                      unassignTarget.companyName ||
                                      'this vendor'
                                  }"`
                                : '""'}
                        </strong>{' '}
                        from your bank? This removes the vendor from your list. Global catalog entries are not deleted.
                    </p>
                    <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
                        <Button type="button" variant="secondary" disabled={unassignSubmitting} onClick={closeUnassignModal}>
                            No
                        </Button>
                        <Button
                            type="button"
                            variant="danger"
                            disabled={unassignSubmitting || !unassignTarget}
                            onClick={() => unassignTarget && executeUnassignCompany(unassignTarget)}
                        >
                            {unassignSubmitting ? 'Removing...' : 'Yes'}
                        </Button>
                    </div>
                </div>
            </Modal>

            <CompanyDetailsModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                company={selectedCompany}
                bankId={bankId || undefined}
                onUpdate={async () => {
                    if (!uid) return;
                    try {
                        const companiesData = await fetchCompaniesData(uid, isBankUser);
                        setCompanies(companiesData);
                        // Extract unique company types
                        const typesSet = new Set<string>();
                        companiesData.forEach(company => {
                            if (company.companyType) {
                                typesSet.add(company.companyType);
                            }
                        });
                        setCompanyTypes(Array.from(typesSet));
                        showToast('✅ Company updated successfully!', 'success');
                    } catch (err: any) {
                        showToast('❌ Failed to refresh companies', 'error');
                    }
                }}
            />

        </>
    );
}

